import {
	Box,
	Flex,
	Grid,
	GridItem,
	IconButton,
	VStack,
} from '@chakra-ui/react';
import type { SingleValue } from 'chakra-react-select';
import type React from 'react';
import { useMemo } from 'react';
import { useFieldArray } from 'react-hook-form';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftSelect from '@/components/Atoms/FaceliftSelect/FaceliftSelect';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import FaceliftText from '@/components/Atoms/FaceliftText';
import FaceliftTextArea from '@/components/Atoms/FaceliftTextArea';
import FaceliftDatePicker, {
	getTodayInNYTimezone,
} from '@/components/Molecules/FaceliftDatePicker';
import Show from '@/components/Molecules/Show/Show';
import type { ReturnOrderFormData } from '@/components/Templates/Returns/ReturnOrderForm/types';
import { useColorModeValue } from '@/components/ui/color-mode-utils';
import { Tooltip } from '@/components/ui/tooltip';
import { useGetUnassignedTissues } from '@/hooks/service-hooks/returns';
import type { FormMethods } from '@/types/form';
import type { UnassignedTissue } from '@/types/service';

export interface TissueDetailsProps {
	formMethods: FormMethods<ReturnOrderFormData>;
}

const TissueDetails: React.FC<TissueDetailsProps> = ({ formMethods }) => {
	const { watch, setValue, register, control, errors } = formMethods;

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'products',
	});

	// Color mode values
	const deleteButtonHoverBg = useColorModeValue('red.50', 'red.900');
	const deleteButtonHoverBorder = useColorModeValue('red.600', 'red.700');
	const policyTextColor = useColorModeValue('gray.600', 'gray.400');

	// Get clinic name from form
	const selectedClinic = watch('clinic') as SelectItem;
	const clinicName = selectedClinic?.label || '';

	// Fetch unassigned tissues based on selected clinic
	const { data: tissuesData, isLoading: tissuesLoading } =
		useGetUnassignedTissues(clinicName, !!clinicName);

	// Transform tissues data to SelectItem format
	const tissueItems = useMemo(() => {
		if (!tissuesData?.data) return [];
		return tissuesData.data.map((tissue: UnassignedTissue) => ({
			value: tissue.tissueid,
			label: tissue.tissueid,
		}));
	}, [tissuesData]);

	// Get filtered tissue items for a specific product index
	// Excludes tissue IDs that are already selected in other products
	const getFilteredTissueItems = (currentIndex: number) => {
		const allProducts = watch('products') || [];
		const selectedTissueIds = allProducts
			.map((product, idx) =>
				idx !== currentIndex ? product.ta_tissueid : null,
			)
			.filter((id): id is string => !!id);

		return tissueItems.filter(
			(tissue: { value: string }) =>
				!selectedTissueIds.includes(tissue.value as string),
		);
	};

	return (
		<Box mb={-4}>
			<FaceliftText
				fontSize="sm"
				fontWeight="semibold"
				mb={2}
				fontStyle="italic"
			>
				Tissue Details
			</FaceliftText>

			<VStack gap={3} align="stretch">
				{fields.map((field, index) => (
					<Box key={field.id} mt={index > 0 ? 6 : 0}>
					{/* Delete Button - Above fields */}
					<Show when={index > 0}>
						<Flex justify="flex-end" mb={0.5}>
							<Tooltip content="Delete Product" positioning={{ placement: 'top' }}>
								<IconButton
									aria-label="Remove product"
									onClick={() => remove(index)}
									variant="outline"
									colorPalette="red"
									size="sm"
									_hover={{
										bg: deleteButtonHoverBg,
										borderColor: deleteButtonHoverBorder,
									}}
								>
									<FiTrash2 />
								</IconButton>
							</Tooltip>
						</Flex>
					</Show>

						{/* Row 1: Tissue ID, Expiration Date */}
						<Grid
							templateColumns={{
								base: '1fr',
								md: 'repeat(2, 1fr)',
							}}
							gap={3}
							alignItems="end"
						>
							<GridItem>
								<FaceliftSelect<SelectItem, false>
									topLabel="Tissue ID"
									required
									placeholder={
										clinicName ? 'Select Tissue ID' : 'Select clinic first'
									}
									value={
										tissueItems.find(
											(item: SelectItem) =>
												item.value === watch(`products.${index}.ta_tissueid`),
										) || null
									}
									onChange={(newValue: SingleValue<SelectItem>) => {
										setValue(
											`products.${index}.ta_tissueid`,
											String((newValue as SelectItem)?.value || ''),
											{ shouldValidate: true }
										);
									}}
									items={getFilteredTissueItems(index)}
									loading={tissuesLoading}
									disabled={!clinicName}
									error={errors?.products?.[index]?.ta_tissueid?.message}
								/>
							</GridItem>

							<GridItem>
								<FaceliftText fontSize="xs" mb={1} fontWeight="medium">
									Expiration Date
									<Box as="span" color="red.500" ml={1}>
										*
									</Box>
								</FaceliftText>
								<FaceliftDatePicker
									placeholder="mm/dd/yyyy"
									value={watch(`products.${index}.ta_expirationdate`)}
									onChange={(date: string) =>
										setValue(`products.${index}.ta_expirationdate`, date, { shouldValidate: true })
									}
									minDate={getTodayInNYTimezone()}
									defaultToToday
									error={errors?.products?.[index]?.ta_expirationdate?.message}
								/>
							</GridItem>
						</Grid>

						{/* Row 2: Date Return/Transferred, New Shipping Date Requested */}
						<Grid
							templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
							gap={3}
							mt={2}
						>
							<GridItem>
								<FaceliftText fontSize="xs" mb={1} fontWeight="medium">
									Date Return/Transferred
									<Box as="span" color="red.500" ml={1}>
										*
									</Box>
								</FaceliftText>
								<FaceliftDatePicker
									placeholder="mm/dd/yyyy"
									value={watch(`products.${index}.ta_datereturnedtransferred`)}
									onChange={(date: string) =>
										setValue(
											`products.${index}.ta_datereturnedtransferred`,
											date,
											{ shouldValidate: true }
										)
									}
									minDate={getTodayInNYTimezone()}
									defaultToToday
									error={
										errors?.products?.[index]?.ta_datereturnedtransferred?.message
									}
								/>
							</GridItem>

							<GridItem>
								<FaceliftText fontSize="xs" mb={1} fontWeight="medium">
									New Shipping Date Requested
									<Box as="span" color="red.500" ml={1}>
										*
									</Box>
								</FaceliftText>
								<FaceliftDatePicker
									placeholder="mm/dd/yyyy"
									value={watch(`products.${index}.ta_newshippingdaterequested`)}
									onChange={(date: string) =>
										setValue(
											`products.${index}.ta_newshippingdaterequested`,
											date,
											{ shouldValidate: true }
										)
									}
									minDate={getTodayInNYTimezone()}
									defaultToToday
									error={
										errors?.products?.[index]?.ta_newshippingdaterequested?.message
									}
								/>
							</GridItem>
						</Grid>
					</Box>
				))}

				{/* Add Product Button */}
				<Flex justify="flex-start" mt={1}>
					<FaceliftButton
						variant="outline"
						size="sm"
						onClick={() =>
							append({
								ta_tissueid: '',
								ta_expirationdate: '',
								ta_datereturnedtransferred: '',
								ta_newshippingdaterequested: '',
							})
						}
					>
						<Flex align="center" gap={2}>
							<FiPlus />
							<span>Add Product</span>
						</Flex>
					</FaceliftButton>
				</Flex>

				{/* Additional Items Text */}
				<FaceliftText fontSize="xs" fontStyle="italic" mt={2}>
					Please use the comments space below for additional items being
					returned.
				</FaceliftText>

				{/* Return Policy */}
				<Box>
					<FaceliftText fontSize="xs" mb={1}>
						<strong>Return Policy:</strong>
					</FaceliftText>
					<FaceliftText fontSize="xs" color={policyTextColor} mb={2}>
						Customer may return product to MindSight within 30 days of the
						original invoice date. No goods shall be returned for credit without
						first obtaining authorization from Seller's Customer Service
						department. In order for the product to qualify for a reimbursement
						or credit of the amount paid by customer, product must be in its
						original condition, unopened, have no markings, and have
						confirmation that it was properly stored. Frozen tissue are not able
						to be accepted for return.
					</FaceliftText>
				</Box>

				{/* Comments/Clarifications */}
				<FaceliftTextArea
					placeholder="Type Here"
					{...register('ta_commentsclarifications')}
					rows={3}
					minHeight="50px"
				/>
			</VStack>
		</Box>
	);
};

export default TissueDetails;
